import { inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot } from '@angular/router';
import { SessionService } from '@core/services/use-cases/session.service';
import { UserRole } from '@core/entities/models/role.model';
import { AppRoutes } from '@core/constants/app.routes';

/**
 * Guard para proteger rutas según el rol del usuario.
 *
 * Uso en las rutas:
 * ```
 * {
 *   path: 'countries',
 *   canActivate: [roleGuard],
 *   data: { roles: [UserRole.ADMIN] }
 * }
 * ```
 */
export const roleGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const sessionService = inject(SessionService);
  const router = inject(Router);

  // Verificar si hay sesión activa
  const token = sessionService.getToken();
  if (!token) {
    // No hay sesión, redirigir a login
    return router.createUrlTree(['/', AppRoutes.auth, AppRoutes.login]);
  }

  // Obtener el usuario actual
  const currentUser = sessionService.getCurrentUser();
  if (!currentUser) {
    // No hay usuario en sesión, redirigir a login
    sessionService.clearSession();
    return router.createUrlTree(['/', AppRoutes.auth, AppRoutes.login]);
  }

  // Obtener los roles permitidos desde la configuración de la ruta
  const allowedRoles = route.data['roles'] as UserRole[] | undefined;

  // Si no se especificaron roles, permitir acceso (ruta pública autenticada)
  if (!allowedRoles || allowedRoles.length === 0) {
    return true;
  }

  // Verificar si el rol del usuario está en los roles permitidos
  if (allowedRoles.includes(currentUser.role)) {
    return true;
  }

  // Redirigir al dashboard apropiado según el rol
  const redirectUrl = getRedirectUrlForRole(currentUser.role);
  return router.createUrlTree(redirectUrl);
};

/**
 * Obtiene la URL de redirección para cada rol
 */
function getRedirectUrlForRole(role: UserRole): string[] {
  switch (role) {
    case UserRole.ADMIN:
      return ['/', AppRoutes.admin, AppRoutes.countries];
    case UserRole.COUNTRY_ADMIN:
      // TODO: Get countryId from user data and redirect to specific country
      return [
        '/',
        AppRoutes.admin,
        AppRoutes.countries,
        'placeholder-country-id',
        AppRoutes.partners,
      ];
    case UserRole.PARTNER_USER:
      // TODO: Get partnerId from user data and redirect to specific partner
      return [
        '/',
        AppRoutes.admin,
        AppRoutes.partners,
        'placeholder-partner-id',
        AppRoutes.beneficiaries,
      ];
    default:
      return ['/', AppRoutes.auth, AppRoutes.login];
  }
}
